/**
 * Course Resource Extractor
 *
 * Fetches extracted links from Google Docs and YouTube videos from playlists
 * using the Google Apps Script endpoint
 */

const EXTRACTOR_URL = process.env.REACT_APP_RESOURCE_EXTRACTOR_URL;
const EXTRACTOR_MAX_ATTEMPTS = 2;
const EXTRACTOR_RETRY_DELAY_MS = 500;
const WARMUP_STOP_WORDS = new Set([
  'a',
  'an',
  'and',
  'are',
  'as',
  'at',
  'be',
  'by',
  'for',
  'from',
  'how',
  'in',
  'into',
  'is',
  'it',
  'of',
  'on',
  'or',
  'that',
  'the',
  'their',
  'this',
  'to',
  'what',
  'when',
  'where',
  'which',
  'who',
  'why',
  'with',
]);
const OPEN_ENDED_STARTERS = [
  'how ',
  'why ',
  'what might',
  'what should',
  'what does',
  'what happens',
  'in what ways',
  'to what extent',
  'should ',
  'could ',
  'when might',
];

const normalizePromptText = (value) =>
  String(value || '')
    .replace(/\s+/g, ' ')
    .trim();

const normalizeComparisonText = (value) =>
  normalizePromptText(value)
    .toLowerCase()
    .replace(/[^\w\s]/g, ' ');

const buildContentWords = (value) =>
  Array.from(
    new Set(
      normalizeComparisonText(value)
        .split(' ')
        .filter((word) => word.length >= 4 && !WARMUP_STOP_WORDS.has(word))
    )
  );

const buildOverlapRatio = (source, target) => {
  const sourceWords = buildContentWords(source);
  const targetWords = new Set(buildContentWords(target));
  if (sourceWords.length === 0) return 0;
  const matches = sourceWords.filter((word) => targetWords.has(word)).length;
  return matches / sourceWords.length;
};

const isLikelyWarmupQuestion = (value) => {
  const text = normalizePromptText(value);
  if (!text) return false;
  const lowered = text.toLowerCase();
  if (text.endsWith('?')) return true;
  if (OPEN_ENDED_STARTERS.some((starter) => lowered.startsWith(starter))) {
    return true;
  }
  return false;
};

const cleanWarmupQuestionText = (value) =>
  normalizePromptText(String(value || '').replace(/^[\d\-*.)\s]+/, ''));

const sleep = (ms) => new Promise((resolve) => setTimeout(resolve, ms));

const buildExtractorPreview = (value, maxLength = 180) =>
  String(value || '')
    .replace(/\s+/g, ' ')
    .trim()
    .slice(0, maxLength);

const buildCoursePayload = (course) => ({
  id: course.id,
  name: course.name,
  description: course.description,
  warmups: course.warmups,
  extra: course.extra,
  curriculum: course.curriculum,
  youtube: course.youtube,
  extrayoutube: course.extrayoutube,
  extrayoutube1: course.extrayoutube1,
});

const buildExtractorUrl = (coursePayload, attempt) => {
  const url = new URL(EXTRACTOR_URL);
  url.searchParams.set('type', 'course');
  url.searchParams.set('courseData', JSON.stringify(coursePayload));
  url.searchParams.set('_requestTs', String(Date.now()));
  url.searchParams.set('_attempt', String(attempt));
  return url.toString();
};

const createExtractorResponseError = ({
  message,
  response,
  rawText,
  requestUrl,
}) => {
  const status = response?.status ? `status=${response.status}` : null;
  const contentType = response?.headers?.get('content-type') || null;
  const finalUrl = response?.url || requestUrl || EXTRACTOR_URL;
  const snippet = buildExtractorPreview(rawText);
  const detail = [status, contentType ? `content-type=${contentType}` : null, `url=${finalUrl}`, snippet ? `body=${snippet}` : null]
    .filter(Boolean)
    .join(' | ');
  return new Error(detail ? `${message}: ${detail}` : message);
};

async function requestCourseExtraction(course) {
  const coursePayload = buildCoursePayload(course);
  let lastError = null;

  for (let attempt = 1; attempt <= EXTRACTOR_MAX_ATTEMPTS; attempt += 1) {
    const url = buildExtractorUrl(coursePayload, attempt);
    try {
      const response = await fetch(url, {
        cache: 'no-store',
        credentials: 'omit',
      });
      const rawText = await response.text();

      if (!response.ok) {
        throw createExtractorResponseError({
          message: 'Resource extractor request failed',
          response,
          rawText,
          requestUrl: url,
        });
      }

      let data = null;
      try {
        data = JSON.parse(rawText);
      } catch (error) {
        throw createExtractorResponseError({
          message: 'Resource extractor returned non-JSON content',
          response,
          rawText,
          requestUrl: url,
        });
      }

      if (!data || typeof data !== 'object') {
        throw new Error('Resource extractor returned an empty response.');
      }

      return data;
    } catch (error) {
      lastError = error;
      if (attempt >= EXTRACTOR_MAX_ATTEMPTS) {
        throw error;
      }
      console.warn(
        `Retrying resource extractor for ${course.name} after attempt ${attempt} failed:`,
        error?.message || error
      );
      await sleep(EXTRACTOR_RETRY_DELAY_MS * attempt);
    }
  }

  throw lastError || new Error('Resource extractor failed.');
}

/**
 * Extract all resources for a course
 */
export async function extractCourseResources(course) {
  if (!EXTRACTOR_URL) {
    console.warn('Resource extractor URL not configured');
    return null;
  }

  try {
    console.log(`📥 Extracting resources for course: ${course.name}`);
    console.log('  - YouTube:', course.youtube ? 'Yes' : 'No');
    console.log('  - Warmups:', course.warmups ? 'Yes' : 'No');
    console.log('  - Curriculum:', course.curriculum ? 'Yes' : 'No');
    console.log('  - Calling Google Apps Script...');
    const data = await requestCourseExtraction(course);

    console.log('  - Response received:', data.success ? 'Success' : 'Failed');

    if (!data.success) {
      console.error('Resource extraction failed:', data.error);
      return null;
    }

    const counts = {
      descriptionLinks: data.resources.description.links?.length || 0,
      warmupsLinks: data.resources.warmups.links?.length || 0,
      warmupsQuestions: data.resources.warmups.questions?.length || 0,
      extraLinks: data.resources.extra.links?.length || 0,
      curriculumLinks: data.resources.curriculum.links?.length || 0,
      youtubeVideos: data.resources.youtube.videos?.length || 0
    };

    console.log(`✓ Extracted resources for ${course.name}:`, counts);

    // Debug: show actual data structure if nothing was extracted
    const totalExtracted = Object.values(counts).reduce((a, b) => a + b, 0);
    if (totalExtracted === 0) {
      console.warn(`  ⚠️ No resources extracted for ${course.name}. Response:`, data.resources);
    } else {
      // Debug: show sample curriculum links if they exist
      if (data.resources.curriculum?.links && data.resources.curriculum.links.length > 0) {
        console.log(`  📄 Sample curriculum links for ${course.name}:`);
        data.resources.curriculum.links.slice(0, 3).forEach((link, i) => {
          console.log(`    ${i + 1}. Text: "${link.text}", URL: "${link.url}"`);
        });
      }
    }

    return data.resources;
  } catch (error) {
    console.error('Error extracting course resources:', error);
    return null;
  }
}

/**
 * Extract resources for multiple courses and filter by relevance
 */
export async function extractResourcesForCourses(courses, lessonTopic) {
  if (!courses || courses.length === 0) {
    return [];
  }

  console.log(`Extracting resources for ${courses.length} courses...`);

  // Extract resources for each course
  const extractionPromises = courses.map(course =>
    extractCourseResources(course)
      .then(resources => ({
        course,
        resources
      }))
      .catch(error => {
        console.error(`Failed to extract resources for ${course.name}:`, error);
        return { course, resources: null };
      })
  );

  const results = await Promise.all(extractionPromises);

  // Filter out failed extractions
  const successfulExtractions = results.filter(r => r.resources !== null);

  console.log(`Successfully extracted resources from ${successfulExtractions.length}/${courses.length} courses`);

  return successfulExtractions;
}

/**
 * Select most relevant resources based on lesson topic
 * Uses weighted keyword matching with proper noun and phrase detection
 */
export function selectRelevantResources(extractedResources, lessonTopic, maxItems = 10, keywords = null, options = {}) {
  // Use provided keywords or extract them (for backward compatibility)
  let topicKeywords;
  if (keywords) {
    topicKeywords = keywords;
  } else {
    // Fallback to simple extraction if no keywords provided
    const topicLower = lessonTopic.toLowerCase();
    topicKeywords = topicLower
      .split(/\s+/)
      .filter(word => word.length > 3)
      .map(word => ({ text: word, weight: 1.0, type: 'keyword' }));
  }

  const allLinks = [];
  const allVideos = [];

  const normalizeCourseKey = (value) => String(value || '').trim().toLowerCase();
  const preferredCourseId = options?.preferredCourseId
    ? String(options.preferredCourseId).trim()
    : '';
  const preferredCourseKey = normalizeCourseKey(options?.preferredCourseName);
  const isPreferredCourse = (course) => {
    if (!course) return false;
    if (preferredCourseId && course?.id !== undefined && course?.id !== null) {
      if (String(course.id).trim() === preferredCourseId) {
        return true;
      }
    }
    if (preferredCourseKey && course?.name) {
      return normalizeCourseKey(course.name) === preferredCourseKey;
    }
    return false;
  };
  const isPreferredLink = (link) => {
    if (!link) return false;
    if (preferredCourseId && link.courseId !== undefined && link.courseId !== null) {
      return String(link.courseId).trim() === preferredCourseId;
    }
    if (preferredCourseKey && link.courseName) {
      return normalizeCourseKey(link.courseName) === preferredCourseKey;
    }
    return false;
  };

  extractedResources.forEach(({ course, resources }) => {
    const preferredCourseMatch = isPreferredCourse(course);
    // Add links from Google Docs
    ['description', 'warmups', 'extra', 'curriculum'].forEach(docType => {
      // First, add the Google Doc URL itself if it exists
      if (docType !== 'warmups' && resources[docType]?.url && course[docType]) {
        const docName = docType.charAt(0).toUpperCase() + docType.slice(1);
        const docTitle = `${course.name} ${docName} Document`;
        const relevanceScore = calculateLinkRelevance(
          { text: docTitle, url: course[docType], type: 'google_doc' },
          topicKeywords
        );

        allLinks.push({
          type: 'link',
          source: `${course.name} - ${docType}`,
          courseId: course.id,
          courseName: course.name,
          docType,
          text: docTitle,
          url: course[docType],
          relevanceScore: relevanceScore + 10 // Bonus for the main document
        });
      }

      // Then add links extracted FROM inside the Google Doc
      if (resources[docType]?.links) {
        resources[docType].links.forEach(link => {
          if (docType === 'warmups' && isLikelyWarmupQuestion(link?.text)) {
            return;
          }
          const relevanceScore = calculateLinkRelevance(link, topicKeywords);
          const minThreshold = docType === 'extra' && preferredCourseMatch ? 0 : 10;
          // Minimum threshold of 10 to filter out weak/generic matches
          if (relevanceScore >= minThreshold) {
            allLinks.push({
              type: 'link',
              source: `${course.name} - ${docType}`,
              courseId: course.id,
              courseName: course.name,
              docType,
              ...link,
              relevanceScore
            });
          }
        });
      }
    });

    // Add YouTube videos
    ['youtube', 'extrayoutube', 'extrayoutube1'].forEach(playlistType => {
      if (resources[playlistType]?.videos) {
        resources[playlistType].videos.forEach(video => {
          const relevanceScore = calculateVideoRelevance(video, topicKeywords);
          // Minimum threshold of 15 for videos (higher than links due to higher base scores)
          if (relevanceScore >= 15) {
            allVideos.push({
              type: 'video',
              source: `${course.name} - YouTube`,
              courseId: course.id,
              courseName: course.name,
              playlistType,
              ...video,
              relevanceScore
            });
          }
        });
      }
    });
  });

  // Sort each type by relevance
  const sortedLinks = allLinks.sort((a, b) => b.relevanceScore - a.relevanceScore);
  const sortedVideos = allVideos.sort((a, b) => b.relevanceScore - a.relevanceScore);

  const extensionLinks = sortedLinks.filter((link) => link.docType === 'extra');
  const preferredExtensionLinks = extensionLinks.filter(isPreferredLink);
  const hasPreferredExtension = preferredExtensionLinks.length > 0;
  const extensionPool = hasPreferredExtension ? preferredExtensionLinks : extensionLinks;
  const extensionDocLink = extensionPool.find((link) => link.type === 'google_doc') || null;
  const extensionCandidates = extensionDocLink
    ? extensionPool.filter((link) => link.url !== extensionDocLink.url)
    : extensionPool;
  const minExtensionLinks = Math.min(3, extensionPool.length);
  const selectedExtensionLinks = [];
  if (extensionDocLink) {
    selectedExtensionLinks.push(extensionDocLink);
  }
  if (selectedExtensionLinks.length < minExtensionLinks) {
    selectedExtensionLinks.push(...extensionCandidates.slice(0, minExtensionLinks - selectedExtensionLinks.length));
  }

  // Ensure we get a balanced mix: at least 40% should be document links
  const minLinks = Math.ceil(maxItems * 0.4); // At least 40% documents
  const minVideos = Math.floor(maxItems * 0.3); // At least 30% videos

  const filteredLinks = hasPreferredExtension
    ? sortedLinks.filter((link) => link.docType !== 'extra' || isPreferredLink(link))
    : sortedLinks;

  let selectedLinks = filteredLinks.slice(0, Math.max(minLinks, Math.floor(maxItems / 2)));
  let selectedVideos = sortedVideos.slice(0, Math.max(minVideos, Math.floor(maxItems / 2)));

  // Combine and trim to maxItems
  const combined = [...selectedLinks, ...selectedVideos]
    .sort((a, b) => b.relevanceScore - a.relevanceScore)
    .slice(0, maxItems);

  const merged = [];
  const seen = new Set();
  const addUnique = (item) => {
    if (!item || !item.url) return;
    const key = `${item.type}:${item.url}`;
    if (seen.has(key)) return;
    seen.add(key);
    merged.push(item);
  };

  selectedExtensionLinks.forEach(addUnique);
  combined.forEach(addUnique);

  const finalSelection = merged.slice(0, maxItems);

  console.log(`Selected ${finalSelection.length} resources: ${selectedLinks.length} docs, ${selectedVideos.length} videos (out of ${allLinks.length} total docs, ${allVideos.length} total videos)`);
  if (selectedExtensionLinks.length > 0) {
    console.log(`Included ${selectedExtensionLinks.length} extension link(s) from honors/extra docs`);
  }

  return finalSelection;
}

/**
 * Calculate relevance score for a link using weighted keywords
 */
function calculateLinkRelevance(link, topicKeywords) {
  let score = 0;
  const textLower = link.text?.toLowerCase() || '';
  const urlLower = link.url?.toLowerCase() || '';

  topicKeywords.forEach(keyword => {
    const keywordText = keyword.text || keyword; // Support both weighted and simple keywords
    const keywordWeight = keyword.weight || 1.0;

    // Exact keyword match in title (weighted by keyword importance)
    if (textLower.includes(keywordText)) {
      score += 15 * keywordWeight;
    }

    // Partial keyword match in title (only for longer keywords)
    if (keywordText.length > 5) {
      const partialKeyword = keywordText.substring(0, Math.min(keywordText.length - 1, 5));
      if (partialKeyword.length >= 4 && textLower.includes(partialKeyword) && !textLower.includes(keywordText)) {
        score += 8 * keywordWeight;
      }
    }

    // Keyword in URL (lower priority, weighted)
    if (urlLower.includes(keywordText)) {
      score += 5 * keywordWeight;
    }
  });

  // Bonus for curriculum documents (most useful for lesson planning)
  if (link.docType === 'curriculum') score += 12;
  if (link.docType === 'warmups') score += 8;
  if (link.docType === 'extra') score += 6;

  // Bonus for certain link types
  if (link.type === 'google_doc') score += 8;
  if (link.type === 'youtube') score += 4;

  // Base score to ensure we always have some resources (reduced to favor better matches)
  score += 3;

  return score;
}

/**
 * Calculate relevance score for a video using weighted keywords
 */
function calculateVideoRelevance(video, topicKeywords) {
  let score = 0;
  const titleLower = video.title?.toLowerCase() || '';
  const descLower = video.description?.toLowerCase() || '';

  topicKeywords.forEach(keyword => {
    const keywordText = keyword.text || keyword; // Support both weighted and simple keywords
    const keywordWeight = keyword.weight || 1.0;

    // Exact keyword match in title (highest priority, weighted)
    if (titleLower.includes(keywordText)) {
      score += 20 * keywordWeight;
    }

    // Partial keyword match in title (only for longer keywords, weighted)
    if (keywordText.length > 5) {
      const partialKeyword = keywordText.substring(0, Math.min(keywordText.length - 1, 5));
      if (partialKeyword.length >= 4 && titleLower.includes(partialKeyword) && !titleLower.includes(keywordText)) {
        score += 10 * keywordWeight;
      }
    }

    // Keyword in description (weighted)
    if (descLower.includes(keywordText)) {
      score += 7 * keywordWeight;
    }
  });

  // Base score for all videos (reduced to favor better matches)
  score += 1;

  return score;
}

/**
 * Format resources for AI prompt
 */
export function formatResourcesForPrompt(resources) {
  if (!resources || resources.length === 0) {
    return '';
  }

  let formatted = '\n\n━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━\n';
  formatted += '## 📚 SPECIFIC RESOURCES TO USE IN THIS LESSON\n';
  formatted += '━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━\n\n';
  formatted += '**INSTRUCTIONS**: Use these EXACT resources throughout your lesson plan. Copy the titles and URLs exactly as shown.\n\n';

  // Group by course
  const byCourse = {};
  resources.forEach(resource => {
    if (!byCourse[resource.courseName]) {
      byCourse[resource.courseName] = {
        links: [],
        videos: []
      };
    }
    if (resource.type === 'link') {
      byCourse[resource.courseName].links.push(resource);
    } else if (resource.type === 'video') {
      byCourse[resource.courseName].videos.push(resource);
    }
  });

  // Format by course
  Object.entries(byCourse).forEach(([courseName, { links, videos }]) => {
    formatted += `### 📖 From "${courseName}" Course\n\n`;

    if (links.length > 0) {
      const coreLinks = links.filter((link) => link.docType !== 'extra');
      const extensionLinks = links.filter((link) => link.docType === 'extra');

      if (coreLinks.length > 0) {
        formatted += '**📄 Core Documents & Links:**\n';
        coreLinks.forEach((link, idx) => {
          console.log(`📎 Including core link - Text: "${link.text}", URL: "${link.url}", Type: ${link.type}`);
          formatted += `${idx + 1}. [${link.text}](${link.url})\n`;
        });
        formatted += '\n';
      }

      if (extensionLinks.length > 0) {
        formatted += '**🚀 Extension Opportunities (Honors / Extra Credit):**\n';
        extensionLinks.forEach((link, idx) => {
          console.log(`📎 Including extension link - Text: "${link.text}", URL: "${link.url}", Type: ${link.type}`);
          formatted += `${idx + 1}. [${link.text}](${link.url})\n`;
        });
        formatted += '\n';
      }
    }

    if (videos.length > 0) {
      formatted += '**🎥 Video Resources:**\n';
      videos.forEach((video, idx) => {
        formatted += `${idx + 1}. [${video.title}](${video.url})\n`;
        if (video.description && video.description.length > 0 && video.description.length < 150) {
          formatted += `   _${video.description}_\n`;
        }
      });
      formatted += '\n';
    }
  });

  formatted += '\n━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━\n';
  formatted += '**⚠️ CRITICAL INSTRUCTIONS**:\n';
  formatted += '1. INTEGRATE these specific resources throughout your lesson plan\n';
  formatted += '2. BUILD your lesson activities AROUND these resources\n';
  formatted += '3. When Extension Opportunities are provided, include them in a dedicated extension/differentiation section\n';
  formatted += '4. Use the EXACT titles and URLs shown above when referencing them\n';
  formatted += '5. Only mention resources you ARE using - do NOT explain which resources you are NOT using\n';
  formatted += '6. The lesson plan should be ready for teachers and students - no meta-commentary\n';
  formatted += '━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━\n';

  return formatted;
}

export function collectWarmUpQuestionCandidates(extractedResources = []) {
  if (!Array.isArray(extractedResources) || extractedResources.length === 0) {
    return [];
  }

  const candidates = [];
  const seen = new Set();

  const pushCandidate = (candidate = {}, course, docUrl, sourceType) => {
    const courseName = course?.name || 'Course';
    const text = cleanWarmupQuestionText(candidate.text || candidate.sourceParagraph || '');
    if (!isLikelyWarmupQuestion(text)) {
      return;
    }

    const dedupeKey = normalizeComparisonText(text);
    if (!dedupeKey || seen.has(dedupeKey)) {
      return;
    }

    seen.add(dedupeKey);
    candidates.push({
      text,
      url: candidate.url || null,
      courseName,
      courseId: course?.id ?? null,
      docUrl: candidate.docUrl || docUrl || null,
      sourceHeading: candidate.sourceHeading || null,
      sourceParagraph: candidate.sourceParagraph || null,
      sourceType,
    });
  };

  extractedResources.forEach(({ course, resources }) => {
    const docUrl = resources?.warmups?.url || null;

    if (Array.isArray(resources?.warmups?.questions)) {
      resources.warmups.questions.forEach((question) =>
        pushCandidate(question, course, docUrl, 'doc_question')
      );
    }

    if (Array.isArray(resources?.warmups?.links)) {
      resources.warmups.links.forEach((link) =>
        pushCandidate(
          {
            text: link?.text || '',
            url: link?.url || null,
            sourceParagraph: link?.text || '',
          },
          course,
          docUrl,
          'linked_prompt'
        )
      );
    }
  });

  return candidates;
}

const scoreWarmupCandidate = (candidate, lessonTopic) => {
  const text = candidate?.text || '';
  const normalizedTopic = normalizePromptText(lessonTopic);
  const topicWords = buildContentWords(normalizedTopic);
  const candidateWords = new Set(buildContentWords(text));
  const lowered = text.toLowerCase();
  let score = 2;

  topicWords.forEach((word) => {
    if (candidateWords.has(word)) {
      score += 8;
    }
  });

  const overlapWithTopic = buildOverlapRatio(text, normalizedTopic);
  score -= overlapWithTopic >= 0.7 ? 5 : overlapWithTopic >= 0.5 ? 2 : 0;

  if (text.endsWith('?')) score += 3;
  if (OPEN_ENDED_STARTERS.some((starter) => lowered.startsWith(starter))) {
    score += 4;
  }

  const wordCount = normalizePromptText(text).split(' ').filter(Boolean).length;
  if (wordCount >= 7 && wordCount <= 20) score += 2;
  if (wordCount > 24) score -= 2;

  return score;
};

/**
 * Select a warm-up question from extracted warmup docs.
 * Prefer adapting actual scraped question-bank prompts over echoing the topic.
 */
export function selectWarmUpFromResources(extractedResources, lessonTopic, options = {}) {
  const candidates = collectWarmUpQuestionCandidates(extractedResources);
  if (candidates.length === 0) {
    return null;
  }

  const preferredCourseName = normalizeComparisonText(options?.preferredCourseName || '');
  const preferredCourseId = String(options?.preferredCourseId || '').trim();
  const scoredWarmups = candidates
    .map((candidate) => {
      let score = scoreWarmupCandidate(candidate, lessonTopic);

      if (preferredCourseName && normalizeComparisonText(candidate.courseName) === preferredCourseName) {
        score += 3;
      }

      if (preferredCourseId && String(candidate.courseId || '').trim() === preferredCourseId) {
        score += 3;
      }

      return {
        ...candidate,
        relevanceScore: score,
      };
    })
    .sort((left, right) => right.relevanceScore - left.relevanceScore);

  console.log(`  📊 Top scored warmups:`, scoredWarmups.slice(0, 5).map((warmup) => ({
    text: warmup.text.substring(0, 70) + (warmup.text.length > 70 ? '...' : ''),
    score: warmup.relevanceScore,
  })));

  const topWarmups = scoredWarmups.slice(0, 3);
  return {
    warmups: topWarmups,
    source: topWarmups[0]?.relevanceScore >= 8 ? 'adapt_existing' : 'inspiration',
  };
}

/**
 * Format selected warmups for AI prompt.
 */
export function formatWarmupsForPrompt(selectedWarmups) {
  if (!selectedWarmups || !selectedWarmups.warmups || selectedWarmups.warmups.length === 0) {
    return '';
  }

  let formatted = '\n\n## SCRAPED WARM-UP QUESTION BANK\n\n';
  formatted += 'These are concrete warm-up questions scraped from the Teach League course library. Reuse their flavor and inquiry style instead of simply restating the lesson topic.\n\n';

  selectedWarmups.warmups.forEach((warmup, index) => {
    formatted += `${index + 1}. **${warmup.text}**\n`;
    formatted += `   - From: "${warmup.courseName}" course\n`;
    if (warmup.docUrl) {
      formatted += `   - Source document: ${warmup.docUrl}\n`;
    }
    if (warmup.sourceHeading) {
      formatted += `   - Source heading: ${warmup.sourceHeading}\n`;
    }
    formatted += '\n';
  });

  formatted += '\n**INSTRUCTIONS**: ';
  if (selectedWarmups.source === 'adapt_existing') {
    formatted += 'Adapt one of these existing questions when possible. Keep the prompt punchy, open-ended, and genuinely inquiry-driving. ';
  } else {
    formatted += 'Use these as inspiration for tone and structure. Only write a fully new warm-up if none of them can be adapted cleanly. ';
  }
  formatted += 'Do not simply echo the lesson topic. The warm-up should open the inquiry, not paraphrase the prompt.\n';

  return formatted;
}

const courseResourceExtractor = {
  extractCourseResources,
  extractResourcesForCourses,
  selectRelevantResources,
  formatResourcesForPrompt,
  collectWarmUpQuestionCandidates,
  selectWarmUpFromResources,
  formatWarmupsForPrompt,
};

export default courseResourceExtractor;
